<script setup lang="ts">
import axios from 'axios';
import { computed, onBeforeMount, ref } from 'vue';
import { loadStripe, type Stripe, type PaymentRequestPaymentMethodEvent } from '@stripe/stripe-js';
import { StripeElement, StripeElements } from 'vue-stripe-js';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { type Coupon, useStation, type VerifyCouponReturnType } from "@/store/station";
import { useUserStore } from "@/store/user";
import { useInvalidCardOverlay } from "@/store/overlays/invalid-card-overlay";
import stripe_logo_url from "@/assets/secured_by_stripe.png";

const router = useRouter();
const station = useStation();
const { t, locale } = useI18n();
const user = useUserStore();
const invalid_card_overlay = useInvalidCardOverlay();

const stripe_key = import.meta.env.VITE_APP_stripeId;
const stripe_loaded = ref<boolean>(false);

const coupon_length = +import.meta.env.VITE_APP_COUPON_LENGTH;

const customer_email = ref<string | null>(null);
const email_field = ref();
const button_enabled = ref<boolean>(true);
const stripe_is_loading = ref<boolean>(false);
const popup_is_visible = ref<boolean>(false);
const can_use_gpay = ref<boolean>(false);
const coupon_code = ref<string>("");

function is_valid_email(v: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
}

const email_rules = [
  (v: string | null) => !v || is_valid_email(v) || t('invalid_email'),
];

/** Stripe stuff **/
const card = ref();
const elms = ref();
const instance_options = ref<any>({});
const elements_options  = computed(() => ({ locale: locale.value }));
const card_options = computed(() => ({ disabled: (station.qrcode ?? -1) <= 0 }));
const gpay_options = computed(() => {
  const custom_appearance = {
    paymentRequestButton: { theme: 'light-outline' },
  };
  return {
    paymentRequest: payment_request.value,
    style: custom_appearance,
  }
});

const payment_request = computed(() => {
  if (!elms.value) {
    return null;
  }
  const stripe: Stripe = elms.value.instance;
  const req = stripe.paymentRequest({
    country: station.country,
    currency: "eur",
    total: {
      label: t("preauth"),
      amount: station.max_wallet_imprint_cents,
      pending: true,
    },
    requestPayerName: true,
    requestPayerEmail: true,
    disableWallets: ["link"],
  });

  (async function(): Promise<void> {
    if (station.allow_gpay) {
      if (!req) {
        return;
      }
      const can_make_payments = await req.canMakePayment();
      can_use_gpay.value = can_make_payments === null
        ? false
        : can_make_payments["googlePay"] || can_make_payments["applePay"]
      await purchase_via_gpay();
    }
  })().catch((e) => {
    console.error('GPay can-use error: ', e) }
  );

  return req;
});

onBeforeMount(() => {
  const stripe_promise = loadStripe(stripe_key);
  stripe_promise.then(() => stripe_loaded.value = true).catch(e => console.error(e));
});


const button_message = computed((): string => {
  return t(button_enabled.value ? "payforcharge" : "pleasewait");
});
const payment_button_color = computed((): string => {
  return button_enabled.value ? "success" : "success darken-2";
});

function unlock_all(): void {
  button_enabled.value = true;
  stripe_is_loading.value = false;
}
function show_popup(): void {
  popup_is_visible.value = true;
}
async function hide_popup(continue_purchase: boolean): Promise<void> {
  popup_is_visible.value = false;
  if (continue_purchase) await purchase(true);
  else unlock_all();
}

async function pre_purchase() {
  if (customer_email.value && !is_valid_email(customer_email.value)) {
    email_field.value?.validate();
    return;
  }
  const enabled = button_enabled.value;
  button_enabled.value = false;
  if (enabled) {
    await purchase();
  }
}

async function complete_login()
{
  if (!station.qrcode) {
    console.log(`[stripe.complete_login] qrcode is ${station.qrcode}`)
    await router.replace("/");
  } else {
    localStorage.setItem('rossini_app_cs_list', station.qrcode.toString());
    await router.replace(`/${station.qrcode}`);
  }
}

function confirm_auth(payment_url: string, payload: PayloadType) {
  const check_confirmation_url = `${payment_url}/confirm_auth/`;
  axios.post(check_confirmation_url, payload)
    .then(complete_login)
    .catch((error: any) => {
      if (error.response) {
        console.error("Error");
        // console.log(error.response.data);
        // console.log(error.response.status);
        // console.log(error.response.headers);
      }
      unlock_all();
    });
}

function handle_card_error(error_code: string): void {
  let error_type;
  switch (error_code) {
    case "invalid_number":
    case "incorrect_number":
    case "incomplete_number":
      error_type = "invalid_number";
      break;
    case "incomplete_expiry":
    case "invalid_expiry_month":
    case "invalid_expiry_year":
      error_type = "invalid_expiry_date";
      break;
    case "incorrect_cvc":
    case "invalid_cvc":
    case "incomplete_cvc":
      error_type = "invalid_cvc";
      break;
    case "incorrect_zip":
    case "incomplete_zip":
      error_type = "incorrect_zip";
      break;
    case "expired_card":
    case "card_declined":
    case "payment_intent_authentication_failure":
      error_type = error_code;
      break;
    default:
      console.error("Got an unexpected error: ", error_code);
      error_type = "generic_error";
  }
  invalid_card_overlay.show(error_type);
  unlock_all();
}

type PayloadType = {
  paymentmethod_id?: string,
  email?: string,
  skip_fingerprint?: boolean,
  coupon_code?: string,
};

async function use_coupon(qrcode: number | null): Promise<Coupon | null | "completed" | "invalid"> {
  const coupon_result: VerifyCouponReturnType = await station.use_coupon(coupon_code.value);
  if (coupon_result === "Error" || coupon_result === "Invalid") {
    invalid_card_overlay.show("bad_coupon");
    unlock_all();
    return "invalid";
  }
  const coupon: Coupon | null = coupon_result;

  if (coupon !== null && coupon.ctype === "FREE") {
    const payload: {
      email?: string,
      coupon_code: string,
    } = {
      coupon_code: coupon_code.value,
    };
    if (customer_email.value) payload.email = customer_email.value
    try {
      await axios.post(`/v2/chargecontroller/${qrcode}/payment/free_coupon/`, payload);
      await complete_login();
      unlock_all();
      return "completed";
    } catch (err: any) {
      switch (err.status) {
        case 404:
          invalid_card_overlay.show("bad_coupon");
          break;
        case 402:
          console.log("[stripe.purchase]: coupon type is not 'free'");
          invalid_card_overlay.show();
      }
      unlock_all();
      return "invalid";
    }
  }
  return coupon;
}

async function purchase(skip_fingerprint: boolean = false): Promise<void> {
  if ((station.qrcode ?? -1) <= 0) {
    console.log(`[stripe] qrcode is ${station.qrcode}`);
    return;
  }
  const qrcode = station.qrcode;

  const coupon_result = await use_coupon(qrcode);
  if (coupon_result === "completed" || coupon_result === "invalid") {
    return;
  }
  const coupon: Coupon | null = coupon_result;

  stripe_is_loading.value = true;
  const email: string = (customer_email.value ?? "") === "" ? "default name" : customer_email.value!;
  const pm_id = await elms.value.instance.createPaymentMethod({
      type: "card",
      card: card.value.stripeElement,
      billing_details: { name: email },
    }).then((result: any): string => result.paymentMethod.id)
    .catch((err: any): null => {
      console.log("><>< Error ><><")
      console.log(JSON.stringify(err))
      return null;
    });
  if (pm_id === null) {
    console.error("[purchase]: impossible to get a payment_method object");
    invalid_card_overlay.show("generic_error");
    unlock_all();
    return;
  }

  const payload: PayloadType = { "paymentmethod_id": pm_id };
  if (customer_email.value && customer_email.value !== "") {
    payload.email = customer_email.value;
  }
  if (coupon !== null) {
    payload.coupon_code = coupon.code;
  }
  if (skip_fingerprint) payload.skip_fingerprint = true;
  let payment_url = `/v2/chargecontroller/${qrcode}/payment`;
  const imprintURL = `${payment_url}/imprint/`;

  try {
    const client_secret = await axios.post(imprintURL, payload);
    console.warn("[purchase]: got response: ", client_secret);
    await elms.value.instance.confirmCardPayment(client_secret.data.client_secret)
      .then(async (result: any): Promise<void> => {
        if (result.paymentIntent) {
          console.log(`[purchase]: received id of type ${typeof(pm_id)}`);
          confirm_auth(payment_url, payload);
        } else {
          handle_card_error(result.error.code);
        }
      })
      .catch((error: any): void => {
        console.log("[stripe-checkout]: got error ", error);
        unlock_all();
        if (error?.response) {
          if (error.response?.status === 402) {
            console.warn(`Server returned error ${error.response.status} with the following payload:`);
            console.warn(error.response);
            invalid_card_overlay.show();
          } else if (error.response?.status == 403) {
            if (error.response?.data?.detail === "Authentication credentials were not provided.") {
              console.warn("[stripe-checkout]: CSRF token error. Logging out twice to clear cookies...");
              // Suppress logout errors
              user.logout().catch(e => console.warn(e));
            }
          } else {
            console.warn(`[stripe-checkout]: server returned error ${error.response.status}`);
          }
        }
      });
  }
  catch (error: any) {
    if (error?.response?.data?.error_code === 2002) {
      console.warn(
        "[stripe-checkout]: multiple attempts with the same card may result in " +
        "the bank keeping the pre-authorisation for up to a week"
      );
      show_popup();
    } else unlock_all();
  }
}

async function purchase_via_gpay() {
  const qrcode = station.qrcode;

  let payment_url = `/v2/chargecontroller/${qrcode}/payment`;
  const imprint_url = `${payment_url}/imprint/`;
  const stripe = elms.value.instance;
  console.log("[stripe]: enabling gpay");
  if (payment_request.value === null) {
    return;
  }
  payment_request.value.on('paymentmethod', async (response: PaymentRequestPaymentMethodEvent) => {
    button_enabled.value = false;

    try {
      const coupon_result = await use_coupon(qrcode);
      if (coupon_result === "completed" || coupon_result === "invalid") {
        // NOTE: response.complete() is not called here, which technically violates the
        // Payment Request API contract (it requires complete() to always be called to
        // dismiss the browser's payment sheet).
        // - "completed": use_coupon already called complete_login() which navigates away,
        //   so the sheet gets torn down by the navigation itself.
        // - "invalid": the sheet is left open without being explicitly dismissed.
        //   Consider calling response.complete('fail') for the "invalid" case.
        return;
      }
      const coupon: Coupon | null = coupon_result;

      const payload: PayloadType = { "paymentmethod_id": response.paymentMethod.id };
      if ((customer_email.value ?? "") !== "") {
        payload.email = customer_email.value || "";
      } else {
        payload.email = response.payerEmail || "";
      }
      if (coupon !== null) {
        payload.coupon_code = coupon.code;
      }
      const client_secret = await axios.post(imprint_url, payload);
      const {paymentIntent, error: confirmError} = await stripe.confirmCardPayment(
        client_secret.data.client_secret,
        {payment_method: response.paymentMethod.id},
        {handleActions: false}
      );
      let succeeded = false;
      if (confirmError) {
        // Report to the browser that the payment failed, prompting it to re-show the payment
        // interface, or show an error message and close the payment interface.
        response.complete('fail');
        unlock_all();
      } else {
        // Report to the browser that the confirmation was successful, prompting
        // it to close the browser payment method collection interface.
        response.complete('success');
        // Check if the PaymentIntent requires any actions and if so let Stripe.js handle the flow
        if (paymentIntent.status === "requires_action") {
          // Let Stripe.js handle the rest of the payment flow.
          const {error} = await stripe.confirmCardPayment(client_secret.data.client_secret);
          if (error) {
            // The payment failed -- ask your customer for a new payment method.
            invalid_card_overlay.show("generic_error");
            unlock_all();
          } else {
            // The payment has succeeded.
            succeeded = true;
          }
        } else {
          // The payment has succeeded.
          succeeded = true;
        }
      }

      if (succeeded) {
        confirm_auth(payment_url, payload);
      }
    } catch (err: any) {
      console.error("[gpay]: unexpected error in paymentmethod handler", err);
      response.complete('fail');
      invalid_card_overlay.show("generic_error");
      unlock_all();
    }
  });
}
</script>

<template>
  <div>
    <v-overlay
      v-model="stripe_is_loading"
      no-click-animation
      persistent
      z-index="1300"
      class="align-center justify-center"
      style="background-color: rgba(0, 0, 0, 0.4)"
    >
      <v-progress-circular color="white" indeterminate size="64" width="8" />
    </v-overlay>
    <v-dialog
      v-model="popup_is_visible"
      no-click-animation
      persistent
      z-index="1300"
      class="align-center justify-center"
      style="background-color: rgba(0, 0, 0, 0.4)"
      @click:outside="hide_popup(false)"
    >
      <v-card elevation="20">
        <v-card-title class="bg-primary text-white">{{ t('card-popup.title') }}</v-card-title>
        <v-divider />
        <v-card-text class="justify-end align-center">
          {{ t('card-popup.text') }}
          <br>
          {{ t('card-popup.refund') }}
        </v-card-text>
        <v-divider />
        <v-card-actions>
          <v-spacer />
          <v-btn elevation="3" class="mr-1 bg-app-bar" @click="hide_popup(false)">
            {{ t('card-popup.button.no') }}
          </v-btn>
          <v-btn elevation="3" class="mr-1 bg-app-bar" @click="hide_popup(true)">
            {{ t('card-popup.button.ok') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <stripe-elements
      v-if="stripe_loaded"
      v-slot="{elements}"
      ref="elms"
      :stripe-key="stripe_key"
      :instance-options="instance_options"
      :elements-options="elements_options"
    >
      <v-container>
        <v-row no-gutters class="align-center justify-center">
          <v-col>
            <stripe-element ref="card" :elements="elements" :options="card_options" />
            <v-divider :thickness="1" class="border-opacity-50 mt-2"/>
          </v-col>
          <v-col cols="2" class="align-center">
            <v-img width="100%" max-width="120px" max-height="40px"
                   :src="stripe_logo_url"
                   class="mr-0 ml-auto" aspect-ratio="16/9" />
          </v-col>
        </v-row>
        <v-row no-gutters>
          <v-col>
            <v-text-field
              ref="email_field"
              v-model="customer_email"
              :label="t('mail')"
              :rules="email_rules"
              validate-on="lazy invalid-input"
              class="pb-4 emailfield"
              hide-details="auto"
              variant="underlined"
              clearable
              prepend-inner-icon="mdi-email"
            />
          </v-col>
        </v-row>
        <!-- COUPON -->
        <template v-if="station.coupon_enabled">
          <v-row no-gutters>
            <v-col class="d-flex justify-center">
              <div style="text-align: center">{{ t("do_u_have_coupon") }}</div>
            </v-col>
          </v-row>
          <v-row no-gutters>
            <v-col class="d-flex justify-center">
              <v-otp-input
                type="text" :length="coupon_length"
                max-height="50px" max-width="220px"
                variant="solo-filled"
                v-model="coupon_code"
                />
            </v-col>
          </v-row>
        </template>
        <!-- BUTTON -->
        <v-row no-gutters>
          <v-col class="d-flex justify-center">
            <v-btn class="ml-0 mr-0" :color="payment_button_color" @click="pre_purchase">
              {{ button_message }}
            </v-btn>
          </v-col>
        </v-row>
        <template v-if="!!payment_request && can_use_gpay">
          <v-row no-gutters>
            <v-col class="d-flex justify-center">
              <b> {{ t("payfaster") }} </b>
            </v-col>
          </v-row>
          <v-row class="mt-1" no-gutters style="border: 1px solid rgb(241 220 220)">
            <v-col class="pb-0">
              <stripe-element
                v-if="!!payment_request && can_use_gpay"
                type="paymentRequestButton"
                :elements="elements" :options="gpay_options"
              />
            </v-col>
          </v-row>
        </template>
      </v-container>
    </stripe-elements>
  </div>
</template>

<i18n lang="yml">
en:
  mail: "Email (optional, to obtain a receipt)"
  payforcharge: "Paid service"
  pleasewait: "Please wait..."
  card-popup:
    title: "Warning"
    text: "You are entering multiple times the same credit card. Each time you retry a pre-authorisation of 50€ will be withdrawn, this may block your credit card, are you sure you want to proceed?"
    refund: "Any amount debited and not used will be re-credited"
    button:
      ok: "Proceed"
      no: "Go back"
  payfaster: "or pay faster with"
  preauth: "Pre-authorization"
  do_u_have_coupon: Do you have a discount code?
  invalid_email: Please enter a valid email address
it:
  mail: "Serve una ricevuta? Ricevetela via mail…"
  payforcharge: "Pagamento e ricarica"
  pleasewait: "Attendi un momento..."
  card-popup:
    title: "Attenzione"
    text: "Avete già effettuato vari tentativi, altre prove potrebbero portare al blocco della carta:  siete sicuri di voler procedere?"
    refund: "Ogni importo addebitato e non fruito verrà riaccreditato."
    button:
      ok: Procedi
      no: Indietro
  payfaster: "o paga più velocemente con"
  preauth: "Pre-autorizzazione"
  do_u_have_coupon: Hai un codice sconto?
  invalid_email: Inserisci un indirizzo email valido
fr:
  mail: "Besoin d’un reçu? Laissez votre email…"
  payforcharge: "Payez pour charger"
  pleasewait: "Patientez s'il-vous-plait..."
  card-popup:
    title: Attention
    text:  "Vous avez renseigné plusieurs fois la même carte de credit. Chaque fois une pré-autorisation de 50€ est prélevée, cela peut induire votre banque à bloquer la carte, êtes vous sûrs de vouloir procéder encore?"
    refund: "Tout montant débité et non utilisé sera re-crédité"
    button:
      ok: "Procéder"
      no: Fermer
  payfaster: "ou reglez plus rapidement avec"
  preauth: "pré-autorisation"
  do_u_have_coupon: Avez vous un code de remise?
  invalid_email: Veuillez saisir une adresse e-mail valide
de:
  mail: "Brauchen Sie eine Quittung? Erhalten Sie es per E-Mail…"
  payforcharge: "Zahlung und aufladung"
  pleasewait: "Moment abwarten..."
  card-popup:
    title: "Achtung"
    text: "Da sie schon einige Versuche unternommen haben, besteht die Gefahr das bei weiteren Versuchen die Karte eingezogen wird. Wollen sie wirklich fortfahren?"
    refund: "Jeder Betrag der nicht verbraucht wird, wird rückerstattet"
    button:
      ok: "Weitergehen"
      no: "Zurückgang"
  payfaster: "oder schneller bezahlen mit"
  preauth: "Vorabgenehmigung"
  do_u_have_coupon: Haben Sie einen Rabattcode?
  invalid_email: Bitte geben Sie eine gültige E-Mail-Adresse ein
nl:
  mail: "Een ontvangstbewijs nodig? Ontvang hem per e-mail…"
  payforcharge: "Betalen om op te laden"
  pleasewait: "Gelieve te wachten..."
  card-popup:
    title: "Waarschuwing"
    text: "U voert meerdere keren dezelfde creditcard in. Elke keer dat u het opnieuw probeert, wordt een pre-autorisatie van € 50 ingetrokken. Dit kan uw creditcard blokkeren. Weet u zeker dat u wilt doorgaan?"
    refund: "Elk bedrag dat is gedebiteerd en niet is gebruikt, wordt opnieuw gecrediteerd"
    button:
      ok: "Doorgaan"
      no: "Ga terug"
  payfaster: "of betaal sneller met"
  preauth: "Pre-autorisatie"
  do_u_have_coupon: Heb je een kortingscode?
  invalid_email: Voer een geldig e-mailadres in
es:
  mail: "¿Necesita un recibo? Recíbalo por correo..."
  payforcharge: "Pago y recarga"
  pleasewait: "Por favor, espere..."
  card-popup:
    title: "Advertencia"
    text: "Está introduciendo varias veces la misma tarjeta de crédito. Cada vez que lo reintente se realizará una preautorización de 50€, lo que puede bloquear su tarjeta. ¿Está seguro de que desea continuar?"
    refund: "Cualquier importe cobrado y no utilizado será reacreditado"
    button:
      ok: "Continuar"
      no: "Volver"
  payfaster: "o pague más rápido con"
  preauth: "Preautorización"
  do_u_have_coupon: ¿Tiene un código de descuento?
  invalid_email: Por favor, introduzca una dirección de correo electrónico válida
</i18n>

<style>
.stripe-card {
  width: 80%;
  margin: 20px auto;
  border: 1px solid grey;
}
.stripe-card.complete {
  border-color: green;
}
.emailfield {
  background-color: rgba(255, 0, 0, 0);
}
</style>

<!-- vim: softtabstop=2:shiftwidth=2
-->
